// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file HoverCard.tsx
 * @input Uses React, useHoverCard hook
 * @output Exports HoverCard component for hover/focus triggered layers
 * @position Layer component; uses inline-safe trigger wrapper and renders the floating layer inline
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/HoverCard/HoverCard.test.tsx
 * - /packages/core/src/HoverCard/index.ts
 * - /apps/storybook/stories/HoverCard.stories.tsx
 * - /packages/cli/templates/blocks/components/HoverCard/ (showcase blocks)
 */
import { useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from "../hooks/useIsomorphicLayoutEffect.js";
import * as stylex from '@stylexjs/stylex';
import { useHoverCard } from "./useHoverCard.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars } from "../theme/tokens.stylex.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Check if children are text-only (no React elements)
 */
function isTextOnly(children) {
  return typeof children === 'string' || typeof children === 'number';
}

/**
 * Utility to merge ARIA ID strings
 */
function mergeIds(...ids) {
  const filtered = ids.filter(Boolean);
  return filtered.length > 0 ? filtered.join(' ') : undefined;
}

/**
 * HoverCard component for displaying interactive content on hover/focus.
 *
 * Uses a display:contents wrapper so children refs are preserved.
 * Uses CSS anchor positioning and the Popover API for optimal performance.
 *
 * @example
 * ```
 * <HoverCard
 *   content={<ProfileCard user={user} />}
 *   placement="above">
 *   <Button>Hover me</Button>
 * </HoverCard>
 * ```
 */
export function HoverCard({
  children,
  content,
  placement = 'above',
  alignment = 'center',
  delay = 300,
  hideDelay = 200,
  focusTrigger = 'auto',
  isEnabled = true,
  onOpenChange,
  hasHoverIndication = 'auto',
  isOpen,
  isDefaultOpen,
  xstyle,
  className,
  style
}) {
  const wrapperRef = useRef(null);
  const textOnly = isTextOnly(children);

  // Determine if hover indication should be shown
  const showHoverIndication = hasHoverIndication === true || hasHoverIndication === 'auto' && textOnly;
  const handleShow = useCallback(() => {
    onOpenChange?.(true);
  }, [onOpenChange]);
  const handleHide = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  // Use the hook for all hover card behavior
  const hoverCard = useHoverCard({
    placement,
    alignment,
    delay,
    hideDelay,
    focusTrigger,
    isEnabled,
    isOpen,
    isDefaultOpen,
    onShow: handleShow,
    onHide: handleHide
  });

  // For element children with display:contents, attach ref to first child
  useIsomorphicLayoutEffect(() => {
    if (textOnly) {
      return;
    } // Skip for text-only (ref is on wrapper)

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }
    const firstChild = wrapper.firstElementChild;
    if (!firstChild) {
      return;
    }

    // Use combined ref for position + interaction
    hoverCard.ref(firstChild);

    // Set aria-describedby, merging with existing
    const existingDescribedBy = firstChild.getAttribute('aria-describedby');
    firstChild.setAttribute('aria-describedby', mergeIds(existingDescribedBy, hoverCard.describedBy) ?? '');
    return () => {
      hoverCard.ref(null);
      if (existingDescribedBy) {
        firstChild.setAttribute('aria-describedby', existingDescribedBy);
      } else {
        firstChild.removeAttribute('aria-describedby');
      }
    };
  }, [textOnly, hoverCard.ref, hoverCard.describedBy]);

  // Render the floating layer inline, in the same place on the server and the
  // client. The layer is a `popover` element opened via the Popover API, so the
  // browser promotes it to the top layer when shown — that already escapes
  // ancestor clipping, stacking, and transform containing-block traps, and CSS
  // anchor positioning resolves the trigger reference regardless of where the
  // element sits in the DOM, so no portal is needed to "escape" layout.
  //
  // The layer renders as inline-safe phrasing markup (a `<span>`, see
  // useHoverCard), which stays put inside a `<p>` instead of being reparented
  // by the HTML parser. That keeps the server markup and the first client
  // render identical, so there is no hydration mismatch — and it preserves the
  // inline-safety guarantee (no block elements injected into a paragraph).
  const renderedHoverCard = hoverCard.renderHoverCard(content, {
    xstyle,
    className,
    style
  });

  // For text-only children: use inline span with ref on wrapper
  if (textOnly) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("span", {
        ref: hoverCard.ref,
        tabIndex: 0,
        "aria-describedby": hoverCard.describedBy,
        ...{
          0: {
            className: "xt0psk2"
          },
          1: {
            className: "xt0psk2 xujl8zx xev0dqp xycaml9 x11b3rvo"
          }
        }[!!showHoverIndication << 0],
        children: children
      }), renderedHoverCard]
    });
  }

  // For element children: use inline-safe display:contents, ref on first child
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ref: wrapperRef,
      ...{
        className: "xjp7ctv"
      },
      children: children
    }), renderedHoverCard]
  });
}
HoverCard.displayName = 'HoverCard';